import React, { useEffect, useMemo } from 'react';
import { Box, Card, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Button, Link, Divider } from '@mui/material';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as ViewIcon } from '../../../assets/SVG/eye_view.svg';
import HeaderProfile from '../../../components/header_Profile/header_Profile.index';
import SummaryCard from './newPharmacy/SummaryCard';
import { useNavigate, useLocation } from 'react-router-dom';
import Grid2 from '@mui/material/Grid2';
import useCurrency from '../../../components/CurrencyProvider';
import { LABEL_SX, BODY_SX, BODY_MULTILINE_SX, TABLE_HEADER_ROW_SX } from '../../../utils/typographyStyles';
import { formatDateDMY } from '../../../utils/dateUtils';
import { usePharmacyInvoiceDetails } from './viewPharmacyInvoice.helper';

// ============================================================================
// Reusable Section Component
// ============================================================================

interface InfoSectionProps {
  label: string;
  value: string;
  multiline?: boolean;
}

const InfoSection: React.FC<InfoSectionProps> = ({ label, value, multiline = false }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Typography sx={multiline ? BODY_MULTILINE_SX : BODY_SX}>{value || '-'}</Typography>
  </Box>
);

const ViewPharmacyInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceId = (location.state as { invoice_id?: number | string | null } | null)?.invoice_id ?? 4; // Temporary default to 4
  const { currencySymbol } = useCurrency();

  // Fetch pharmacy invoice details
  const {
    data: invoiceResponse,
    error: invoiceError,
    isLoading: invoiceLoading,
  } = usePharmacyInvoiceDetails(invoiceId);

  const invoiceDetails = invoiceResponse?.data;

  useEffect(() => {
    if (invoiceDetails) {
      // eslint-disable-next-line no-console
      console.log('ViewPharmacyInvoice: invoice details response', invoiceDetails);
    }
  }, [invoiceDetails]);

  useEffect(() => {
    if (invoiceError) {
      // eslint-disable-next-line no-console 
      console.error('ViewPharmacyInvoice: invoice details error', invoiceError);
    }
  }, [invoiceError]);

  // Transform API data
  const {
    billNo,
    patientId,
    items,
    totals: { totalAmount, concessionAmount, taxAmount, netAmount },
    meta: { concessionReason, dueDate, termsAndConditions, legalMentions, remark, comments },
  } = useMemo(() => {
    if (!invoiceDetails) {
      return {
        billNo: '-',
        patientId: null,
        items: [],
        totals: {
          totalAmount: 0,
          concessionAmount: 0,
          taxAmount: 0,
          netAmount: 0,
        },
        meta: {
          concessionReason: '-',
          dueDate: '-',
          termsAndConditions: '',
          legalMentions: '',
          remark: '',
          comments: '',
        },
      };
    }

    const mappedItems = invoiceDetails.current_prescriptions.map((item) => ({
      visitDate: item.visit_date,
      itemCode: item.item_code,
      itemName: item.item_name,
      packageName: item.package_name || null,
      batchCode: item.batch_number,
      expiryDate: item.expiry_date,
      manufacturer: item.manufacturer,
      availableQtyUom: `${item.remaining_quantity ?? '-'} / ${item.billed_uom || item.prescribed_uom || '-'}`,
      prescribedStrengthQtyUom: `${item.prescribed_strength || '-'} / ${item.prescribed_quantity} / ${item.prescribed_uom}`,
      strengthBilledQtyUom: `${item.billed_strength || item.prescribed_strength || '-'} / ${item.billed_quantity} / ${item.billed_uom}`,
      rackShelfContainer: `Rack: ${item.rack || '-'}\nShelf: ${item.shelf || '-'}\nContainer: ${item.container || '-'}`,
      baseMrp: item.base_mrp,
      totalMrp: item.total_charges,
      concessionAmt: item.concession_amount,
      concessionPercent: item.concession_percentage,
      sgstCgstAmt: item.tax_amount,
      sgstCgstPercent: 0, // Not directly in API
      netAmt: item.net_amount,
      provider: item.provider || '-',
    }));

    return {
      billNo: invoiceDetails.invoice_info.bill_number,
      patientId: invoiceDetails.patient_info.patient_id,
      items: mappedItems,
      totals: {
        totalAmount: Number(invoiceDetails.invoice_info.total_amount) || 0,
        concessionAmount: Number(invoiceDetails.invoice_info.concession_amount) || 0,
        taxAmount: Number(invoiceDetails.invoice_info.tax_amount) || 0,
        netAmount: Number(invoiceDetails.invoice_info.net_amount) || 0,
      },
      meta: {
        concessionReason: invoiceDetails.invoice_info.concession_reason || '-',
        dueDate: invoiceDetails.invoice_info.due_date || '-',
        termsAndConditions: invoiceDetails.invoice_info.terms_and_conditions || '',
        legalMentions: invoiceDetails.invoice_info.legal_mentions || '',
        remark: invoiceDetails.invoice_info.remark || '',
        comments: invoiceDetails.invoice_info.comments || '',
      },
    };
  }, [invoiceDetails]);

  // Show loading state if fetching
  if (invoiceLoading) {
    return (
      <>
        <HeaderProfile />
        <Box sx={{ mx: 2, mt: 3, mb: 3, textAlign: 'center' }}>
          <Typography>Loading pharmacy invoice details...</Typography>
        </Box>
      </>
    );
  }
  return (
    <>
      <HeaderProfile />

      <Box sx={{ mx: 2, mt: 3, mb: 3 }}>
        {/* Top Item Details header card */}
        <Card
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid #E4E7EC',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: 1,
                  color: '#232323',
                }}
              >
                Item
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: '22px',
                  color: '#232323',
                }}
              >
                (Bill No. - {billNo})
              </Typography>
            </Box>

            <Link
              component="button"
              type="button"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                lineHeight: '22px',
                color: '#5A8AEA',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (patientId) {
                  navigate(`/admin/billing/pharmacy/logs/${patientId}`);
                }
              }}
            >
              <ViewIcon style={{ width: 20, height: 20 }} />
              View Invoice Logs 
            </Link>
          </Box>

          <TableContainer sx={{ maxHeight: { xs: 320, md: 480 }, overflowX: 'auto' }}>
            <Table sx={{ borderCollapse: 'collapse', minWidth: 1400 }}>
              <TableHead>
                <TableRow sx={TABLE_HEADER_ROW_SX}>
                  <TableCell sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                    Visit Date
                  </TableCell>
                  <TableCell>Item Code / Item Name</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Package</TableCell>
                  <TableCell>Batch Code / Expiry Date</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Available Qty / UOM</TableCell>
                  <TableCell>Prescribed Strength / Qty. / UOM</TableCell>
                  <TableCell>Strength / Billed Qty. / UOM</TableCell>
                  <TableCell>Rack / Shelf / Container</TableCell>
                  <TableCell align="right">Base MRP ({currencySymbol})</TableCell>
                  <TableCell align="right">Total MRP ({currencySymbol})</TableCell>
                  <TableCell align="right">Concession Amount ({currencySymbol}) / (%)</TableCell>
                  <TableCell align="right">VAT Amount ({currencySymbol})</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell
                    align="right"
                    sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }}
                  >
                    Net Amount ({currencySymbol})
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      background: idx % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                      transition: 'background 120ms ease',
                      '&:hover': { background: '#F4F6F8' },
                      '& td': {
                        borderTop: idx === 0 ? 'none' : undefined,
                        borderBottom: idx === items.length - 1 ? 'none' : undefined,
                      },
                    }}
                  >
                    <TableCell>{item.visitDate ? formatDateDMY(item.visitDate) : '-'}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#E63946' }}>
                          (Rx) Item Code : {item.itemCode}
                        </Typography>
                        <Typography variant="body2">
                          {item.itemName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: item.packageName ? 'primary.main' : 'text.secondary' }}>
                        {item.packageName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.batchCode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expiry Date : {item.expiryDate ? formatDateDMY(item.expiryDate) : '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{item.manufacturer}</TableCell>
                    <TableCell>{item.availableQtyUom}</TableCell>
                    <TableCell>{item.prescribedStrengthQtyUom}</TableCell>
                    <TableCell>{item.strengthBilledQtyUom}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                        {item.rackShelfContainer}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{item.baseMrp.toFixed(2)}</TableCell>
                    <TableCell align="right">{item.totalMrp.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      {item.concessionAmt.toFixed(2)} / ({item.concessionPercent.toFixed(2)}%)
                    </TableCell>
                    <TableCell align="right">
                      {item.sgstCgstAmt.toFixed(2)}
                    </TableCell>
                    <TableCell>{item.provider}</TableCell>
                    <TableCell align="right">{item.netAmt.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Bottom section: left details + right summary */}
        <Grid2 container spacing={3} sx={{ mt: 3, mx: 2 }}>
          <Grid2 size={{ xs: 12, md: 8 }}>
            {/* Concession Reason & Due Date side by side */}
            <Box sx={{ display: 'flex', gap: 12, mb: 3 }}>
              <Box>
                <Typography sx={LABEL_SX}>Concession Reason</Typography>
                <Typography sx={BODY_SX}>{concessionReason}</Typography>
              </Box>
              <Box>
                <Typography sx={LABEL_SX}>Due Date</Typography>
                <Typography sx={BODY_SX}>{dueDate && dueDate !== '-' ? formatDateDMY(dueDate) : '-'}</Typography>
              </Box>
            </Box>

            <InfoSection label="Terms and conditions" value={termsAndConditions} multiline />
            <InfoSection label="Legal Mentions" value={legalMentions} multiline />
            <InfoSection label="Remark" value={remark} multiline />
            {comments && <InfoSection label="Comments" value={comments} multiline />}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <SummaryCard
              total={totalAmount}
              concession={concessionAmount}
              tax={taxAmount}
              net={netAmount}
              showTax={false}
            />
          </Grid2>
        </Grid2>

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              // navigate back to invoice list page
              navigate(`/admin/billing/invoice/patient-invoice/${patientId}`);
            }}
          >
            Back to List
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ViewPharmacyInvoice;
